import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { recordKeys } from '@seen/core/utils'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import { ValidatedToolField } from '#src/components/validated-tool-form/shared/ValidatedToolField.tsx'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface IADLFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  'phone-use': z.string().nullable(),
  shopping: z.string().nullable(),
  'food-preparation': z.string().nullable(),
  housekeeping: z.string().nullable(),
  laundry: z.string().nullable(),
  'mode-of-transportation': z.string().nullable(),
  'medication-responsibility': z.string().nullable(),
  'financial-management': z.string().nullable(),
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.iadlQuestionnaire)

export function IADLForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: IADLFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For determining the level of independence for instrumental activities of daily living."
      form={form}
      response={response}
      {...delegated}
    >
      <ValidatedToolSection title="Choose the item description that most closely resembles the participant's highest functional level.">
        {recordKeys(schema.shape).map((linkId) => (
          <ValidatedToolField
            key={linkId}
            control={form.control}
            name={linkId}
            validatedTool={validatedTool}
            suggestion={fieldSuggestions?.[linkId]}
          />
        ))}
      </ValidatedToolSection>

      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>

      <Score form={form} validatedTool={validatedTool} response={response} />

      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  return (
    <div className="flex flex-col gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
      <p className="text-sm text-foreground-secondary">
        Score ranges from 0 (low function, dependent) to 8 (high function,
        independent) for women and 0 through 5 for men to avoid potential gender
        bias.
      </p>
    </div>
  )
}
